<template>
  <div class="integrated-map">
    <!-- ===== 顶部工具栏 ===== -->
    <div class="map-toolbar">
      <div class="toolbar-left">
        <span class="brand">地图综合演示</span>
        <span class="separator">|</span>
        <span class="subtitle">参照 topevery-dsm-web 架构实现的一体化地图演示</span>
        <span class="separator">|</span>
        <el-tag size="mini" type="warning" effect="plain" class="engine-tag">
          <i class="el-icon-map-location"></i> 高德地图 JS API v2.0
        </el-tag>
      </div>
      <div class="toolbar-right">
        <span class="status-chip">中心: {{ currentCenterText }}</span>
        <span class="status-chip">级别: {{ currentZoom }}</span>
        <span class="status-chip">底图: {{ currentLayerLabel }}</span>
      </div>
    </div>

    <!-- ===== 主体区域 ===== -->
    <div class="map-body">
      <!-- 侧边栏折叠按钮 -->
      <div class="sidebar-toggle" @click="toggleSidebar" :title="sidebarCollapsed ? '展开面板' : '折叠面板'">
        <i :class="sidebarCollapsed ? 'el-icon-s-unfold' : 'el-icon-s-fold'"></i>
      </div>

      <!-- ===== 侧边栏（多标签页） ===== -->
      <transition name="slide-sidebar">
        <div class="map-sidebar" v-show="!sidebarCollapsed">
          <el-tabs v-model="activeTab" tab-position="top" stretch>
            <!-- Tab 1: 基础控制 -->
            <el-tab-pane label="基础控制" name="basic">
              <div class="panel-section">
                <div class="panel-title"><i class="el-icon-s-operation"></i> 底图切换</div>
                <el-radio-group v-model="selectedLayerKey" size="small" @change="applyLayerPreset">
                  <el-radio-button v-for="item in layerOptions" :key="item.key" :label="item.key">{{ item.label }}</el-radio-button>
                </el-radio-group>
              </div>
              <div class="panel-section">
                <div class="panel-title"><i class="el-icon-location-outline"></i> 中心点</div>
                <el-select v-model="selectedCenterKey" size="small" style="width:100%; margin-bottom:8px;">
                  <el-option v-for="item in centerOptions" :key="item.key" :label="item.label" :value="item.key" />
                </el-select>
                <el-button type="primary" size="small" @click="applyCenterPreset" style="width:100%;">切换中心点</el-button>
              </div>
              <div class="panel-section">
                <div class="panel-title"><i class="el-icon-zoom-in"></i> 缩放级别</div>
                <el-slider v-model="selectedZoom" :min="3" :max="18" :step="1" show-input size="small" @change="applyZoomLevel" />
              </div>
              <div class="panel-section">
                <div class="panel-title"><i class="el-icon-search"></i> 点击信息</div>
                <div class="info-grid">
                  <div class="info-item">
                    <span>坐标</span>
                    <strong>{{ clickCoordinateText }}</strong>
                  </div>
                  <div class="info-item">
                    <span>地址</span>
                    <strong class="address-text">{{ clickAddress || '点击地图后自动反查' }}</strong>
                  </div>
                </div>
              </div>
            </el-tab-pane>

            <!-- Tab 2: 数据落点 -->
            <el-tab-pane label="数据落点" name="markers">
              <div class="panel-section">
                <div class="panel-title"><i class="el-icon-position"></i> 点位管理</div>
                <div class="btn-row">
                  <el-button type="primary" size="small" @click="loadBusinessMarkers"><i class="el-icon-plus"></i> 加载示例点</el-button>
                  <el-button size="small" @click="clearBusinessMarkers"><i class="el-icon-delete"></i> 清空</el-button>
                </div>
              </div>
              <div class="panel-section" v-if="businessMarkers.length > 0 || markerAMapInstances.length > 0">
                <div class="panel-title">点位列表 ({{ markerAMapInstances.length }})</div>
                <div class="marker-list">
                  <div
                    class="marker-list-item"
                    v-for="(item, idx) in markerSource"
                    :key="item.id"
                    @click="focusMarker(item)"
                  >
                    <span class="marker-dot" :style="{ background: itemColors[idx % itemColors.length] }"></span>
                    <div class="marker-info">
                      <strong>{{ item.name }}</strong>
                      <span>{{ item.type }}</span>
                    </div>
                    <el-button type="text" size="mini" @click.stop="removeSingleMarker(item)">移除</el-button>
                  </div>
                </div>
              </div>
              <div class="panel-section">
                <div class="panel-title"><i class="el-icon-info"></i> 选中点位</div>
                <div v-if="selectedMarker" class="marker-detail">
                  <div class="detail-row"><span>名称</span><strong>{{ selectedMarker.name }}</strong></div>
                  <div class="detail-row"><span>类型</span><strong>{{ selectedMarker.type }}</strong></div>
                  <div class="detail-row"><span>坐标</span><strong>{{ formatLngLat(selectedMarker.position) }}</strong></div>
                  <div class="detail-row"><span>说明</span><strong>{{ selectedMarker.description }}</strong></div>
                </div>
                <div v-else class="empty-tip">点击点位查看详情</div>
              </div>
            </el-tab-pane>

            <!-- Tab 3: 轨迹回放 -->
            <el-tab-pane label="轨迹回放" name="track">
              <div class="panel-section">
                <div class="panel-title"><i class="el-icon-video-play"></i> 轨迹控制</div>
                <div class="btn-row">
                  <el-button type="primary" size="small" :disabled="isPlaying" @click="startTrackAnimation">
                    <i class="el-icon-video-play"></i> 开始
                  </el-button>
                  <el-button type="warning" size="small" :disabled="!isPlaying" @click="pauseTrackAnimation">
                    <i class="el-icon-video-pause"></i> 暂停
                  </el-button>
                  <el-button type="success" size="small" :disabled="!isPaused" @click="resumeTrackAnimation">
                    <i class="el-icon-video-play"></i> 继续
                  </el-button>
                  <el-button type="danger" size="small" @click="stopTrackAnimation">
                    <i class="el-icon-refresh"></i> 重置
                  </el-button>
                </div>
              </div>
              <div class="panel-section">
                <div class="panel-title"><i class="el-icon-s-grid"></i> 速度控制</div>
                <el-slider v-model="trackSpeed" :min="100" :max="5000" :step="100" show-input size="small" />
                <div class="speed-label">当前: {{ trackSpeed }} ms/步</div>
              </div>
              <div class="panel-section">
                <div class="panel-title"><i class="el-icon-data-line"></i> 轨迹状态</div>
                <div class="info-grid">
                  <div class="info-item"><span>状态</span><strong>{{ trackStatusText }}</strong></div>
                  <div class="info-item"><span>点数</span><strong>{{ trackPath.length }}</strong></div>
                </div>
                <div class="btn-row" style="margin-top:8px;">
                  <el-button size="small" @click="generateNewTrackPath"><i class="el-icon-refresh"></i> 生成新路线</el-button>
                  <el-button size="small" @click="fitTrackView"><i class="el-icon-full-screen"></i> 适配视野</el-button>
                </div>
              </div>
            </el-tab-pane>

            <!-- Tab 4: 坐标转换 -->
            <el-tab-pane label="坐标转换" name="coord">
              <div class="panel-section">
                <div class="panel-title"><i class="el-icon-switch"></i> 坐标转换</div>
                <div class="field-row">
                  <label>原坐标系</label>
                  <el-select v-model="fromCoord" size="small" style="width:100%;">
                    <el-option label="WGS84 (GPS/天地图)" value="wgs84" />
                    <el-option label="GCJ02 (高德/腾讯)" value="gcj02" />
                    <el-option label="BD09 (百度)" value="bd09" />
                  </el-select>
                </div>
                <div class="field-row">
                  <label>输入坐标 (经度,纬度)</label>
                  <el-input v-model="coordInput" placeholder="例如: 116.397428,39.90923" size="small" clearable />
                </div>
                <el-button type="primary" size="small" @click="handleCoordConvert" style="width:100%;">
                  <i class="el-icon-switch"></i> 转换并定位
                </el-button>
                <div v-if="coordResults.show" class="coord-results">
                  <div class="result-row">
                    <span class="result-label">WGS84</span>
                    <el-input :value="coordResults.wgs84" readonly size="mini" />
                  </div>
                  <div class="result-row">
                    <span class="result-label">GCJ02</span>
                    <el-input :value="coordResults.gcj02" readonly size="mini" />
                  </div>
                  <div class="result-row">
                    <span class="result-label">BD09</span>
                    <el-input :value="coordResults.bd09" readonly size="mini" />
                  </div>
                </div>
              </div>
            </el-tab-pane>
          </el-tabs>
        </div>
      </transition>

      <!-- ===== 地图区域 ===== -->
      <div class="map-stage" ref="mapContainer"></div>

      <!-- ===== 底图切换浮层 ===== -->
      <div class="layer-switcher-float">
        <div
          v-for="item in layerOptions"
          :key="item.key"
          class="layer-item"
          :class="{ active: selectedLayerKey === item.key }"
          @click="applyLayerPreset(item.key)"
        >
          {{ item.label }}
        </div>
      </div>
    </div>

    <!-- ===== 底部状态栏 ===== -->
    <div class="map-footer">
      <span><i class="el-icon-location"></i> 鼠标坐标: {{ mouseCoordinateText }}</span>
      <span><i class="el-icon-s-platform"></i> {{ currentLayerLabel }}</span>
      <span class="footer-hint">架构参照: plugin/map → components/map → common-map → views</span>
    </div>
  </div>
</template>

<script>
import AMapLoader from '@amap/amap-jsapi-loader'

const AMAP_KEY = '0826878f934aa6f773237d9c82dfcf45'
const TDT_KEY = '5548e2c926e88b724376d398ba5476ca'

export default {
  name: 'IntegratedMap',

  data() {
    return {
      sidebarCollapsed: false,
      activeTab: 'basic',

      AMap: null,
      map: null,

      selectedLayerKey: 'gaode-vector',
      activeLayers: [],
      layerOptions: [
        { key: 'gaode-vector',   label: '高德矢量' },
        { key: 'gaode-satellite', label: '高德卫星' },
        { key: 'tianditu-vector', label: '天地矢量' },
        { key: 'tianditu-image',  label: '天地影像' },
      ],

      currentCenter: [116.397428, 39.90923],
      currentZoom: 11,
      selectedCenterKey: 'beijing',
      selectedZoom: 11,
      centerOptions: [
        { key: 'beijing',   label: '北京 · 天安门',   center: [116.397428, 39.90923] },
        { key: 'shanghai',  label: '上海 · 人民广场', center: [121.473701, 31.230416] },
        { key: 'guangzhou', label: '广州 · 珠江新城', center: [113.32459,  23.10668]  },
        { key: 'shenzhen',  label: '深圳 · 市民中心', center: [114.057868, 22.543099] },
        { key: 'xuchang',   label: '许昌 · 鄢陵',     center: [114.177394, 34.102132] },
      ],

      clickCoordinateText: '-',
      clickAddress: '',
      clickMarker: null,
      geocoder: null,
      mouseCoordinateText: '-',

      businessMarkers: [],
      selectedMarker: null,
      infoWindow: null,
      markerAMapInstances: [],
      itemColors: ['#409EFF','#67C23A','#E6A23C','#F56C6C','#909399','#9B59B6'],
      markerSource: [
        { id: 'm-1', name: '北京总部',     type: '办公点', position: [116.404, 39.915],  description: '示例总部办公点' },
        { id: 'm-2', name: '朝阳服务站',   type: '服务站', position: [116.453, 39.939],  description: '示例区域服务站' },
        { id: 'm-3', name: '丰台仓配点',   type: '仓储点', position: [116.290, 39.858],  description: '示例仓储配送点' },
        { id: 'm-4', name: '海淀研发中心', type: '研发点', position: [116.338, 39.988],  description: '研发创新中心' },
        { id: 'm-5', name: '通州运营中心', type: '运营点', position: [116.660, 39.902],  description: '运营管理中心' },
      ],

      trackPath: [],
      trackLine: null,
      trackMarker: null,
      passedPolyline: null,
      isPlaying: false,
      isPaused: false,
      isStopped: false,
      trackSpeed: 1000,

      fromCoord: 'wgs84',
      coordInput: '',
      coordResults: {
        show: false,
        wgs84: '',
        gcj02: '',
        bd09: '',
      },
    }
  },

  computed: {
    currentCenterText() {
      return this.formatLngLat(this.currentCenter)
    },
    currentLayerLabel() {
      const t = this.layerOptions.find(i => i.key === this.selectedLayerKey)
      return t ? t.label : '-'
    },
    trackStatusText() {
      if (this.isPlaying) return '播放中'
      if (this.isPaused)   return '已暂停'
      return '已停止'
    },
  },

  mounted() {
    this.initMap()
    this.generateNewTrackPath()
  },

  beforeDestroy() {
    this.destroyTrack()
    this.clearBusinessMarkers()
    if (this.clickMarker && this.map) this.map.remove(this.clickMarker)
    if (this.infoWindow) this.infoWindow.close()
    if (this.map) { this.map.destroy(); this.map = null }
  },

  methods: {
    async initMap() {
      try {
        const AMap = await AMapLoader.load({
          key: AMAP_KEY,
          version: '2.0',
          plugins: ['AMap.Scale', 'AMap.ToolBar', 'AMap.Geocoder', 'AMap.MoveAnimation'],
        })
        this.AMap = AMap

        this.map = new AMap.Map(this.$refs.mapContainer, {
          zoom: this.selectedZoom,
          center: this.currentCenter,
          resizeEnable: true,
          viewMode: '2D',
          mapStyle: 'amap://styles/light',
        })

        this.geocoder = new AMap.Geocoder({ radius: 1000, extensions: 'all' })
        this.infoWindow = new AMap.InfoWindow({ offset: new AMap.Pixel(0, -28) })

        this.map.addControl(new AMap.Scale())
        this.map.addControl(new AMap.ToolBar({ position: 'RT' }))
        this.bindMapEvents()
        this.applyLayerPreset('gaode-vector')
      } catch (e) {
        console.error('地图初始化失败:', e)
        this.$toast.error('高德地图加载失败')
      }
    },

    bindMapEvents() {
      this.map.on('click', this.handleMapClick)
      this.map.on('mousemove', (e) => {
        if (e && e.lnglat) this.mouseCoordinateText = this.formatLngLat([e.lnglat.lng, e.lnglat.lat])
      })
      this.map.on('moveend', this.syncMapState)
      this.map.on('zoomend', this.syncMapState)
    },

    syncMapState() {
      if (!this.map) return
      const c = this.map.getCenter()
      if (c) this.currentCenter = [c.lng, c.lat]
      this.currentZoom = this.map.getZoom()
      this.selectedZoom = this.currentZoom
    },

    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed
      setTimeout(() => { if (this.map) this.map.updateSize() }, 300)
    },

    createTiandituLayer(type) {
      return new this.AMap.TileLayer({
        getTileUrl: (x, y, z) => `https://t0.tianditu.gov.cn/DataServer?T=${type}&x=${x}&y=${y}&l=${z}&tk=${TDT_KEY}`,
        tileSize: 256, zIndex: 4,
      })
    },
    buildLayerGroup(key) {
      if (key === 'gaode-satellite') return [new this.AMap.TileLayer.Satellite(), new this.AMap.TileLayer.RoadNet()]
      if (key === 'tianditu-vector') return [this.createTiandituLayer('vec_w'), this.createTiandituLayer('cva_w')]
      if (key === 'tianditu-image')  return [this.createTiandituLayer('img_w'), this.createTiandituLayer('cia_w')]
      return [new this.AMap.TileLayer()]
    },
    applyLayerPreset(key) {
      if (!this.map || !this.AMap) return
      this.selectedLayerKey = key
      this.activeLayers = this.buildLayerGroup(key)
      this.map.setLayers(this.activeLayers)
    },

    applyCenterPreset() {
      const t = this.centerOptions.find(i => i.key === this.selectedCenterKey)
      if (!t || !this.map) return
      this.map.setCenter(t.center)
      this.currentCenter = [...t.center]
      this.map.setZoom(this.selectedZoom)
    },
    applyZoomLevel() {
      if (!this.map) return
      this.map.setZoom(this.selectedZoom)
      this.currentZoom = this.selectedZoom
    },

    handleMapClick(e) {
      if (!e || !e.lnglat || !this.map) return
      const pos = [e.lnglat.lng, e.lnglat.lat]
      this.clickCoordinateText = this.formatLngLat(pos)
      this.placeClickMarker(pos)
      this.reverseGeocode(pos)
    },
    placeClickMarker(pos) {
      if (!this.map || !this.AMap) return
      if (!this.clickMarker) {
        this.clickMarker = new this.AMap.Marker({ position: pos, offset: new this.AMap.Pixel(-10, -20) })
        this.map.add(this.clickMarker)
      } else {
        this.clickMarker.setPosition(pos)
      }
    },
    reverseGeocode(pos) {
      if (!this.geocoder) return
      this.clickAddress = '解析中...'
      this.geocoder.getAddress(pos, (status, result) => {
        if (status === 'complete' && result && result.regeocode) {
          this.clickAddress = result.regeocode.formattedAddress || '未获取到地址信息'
        } else {
          this.clickAddress = '地址解析失败'
        }
      })
    },

    createBusinessMarker(record, color) {
      const marker = new this.AMap.Marker({
        position: record.position,
        title: record.name,
        offset: new this.AMap.Pixel(-10, -20),
        label: {
          content: `<div style="background:${color};color:#fff;padding:2px 8px;border-radius:12px;font-size:11px;white-space:nowrap;">${record.name}</div>`,
          offset: new this.AMap.Pixel(-10, -30),
          direction: 'top',
        },
      })
      marker.setExtData(record)
      marker.on('click', () => {
        this.selectedMarker = record
        this.infoWindow.setContent(`
          <div style="min-width:200px;line-height:1.8;">
            <div style="font-weight:700;margin-bottom:6px;color:${color};">${record.name}</div>
            <div>类型：${record.type}</div>
            <div>坐标：${this.formatLngLat(record.position)}</div>
            <div>说明：${record.description}</div>
          </div>
        `)
        this.infoWindow.open(this.map, record.position)
        this.activeTab = 'markers'
      })
      return marker
    },
    loadBusinessMarkers() {
      if (!this.map || !this.AMap) return
      this.clearBusinessMarkers()
      this.markerAMapInstances = this.markerSource.map((item, idx) =>
        this.createBusinessMarker(item, this.itemColors[idx % this.itemColors.length])
      )
      this.map.add(this.markerAMapInstances)
      this.map.setFitView(this.markerAMapInstances)
      this.$toast.success(`已加载 ${this.markerAMapInstances.length} 个示例点位`)
    },
    clearBusinessMarkers() {
      if (this.markerAMapInstances.length && this.map) this.map.remove(this.markerAMapInstances)
      this.markerAMapInstances = []
      this.selectedMarker = null
      if (this.infoWindow) this.infoWindow.close()
    },
    removeSingleMarker(record) {
      if (!this.map) return
      const idx = this.markerAMapInstances.findIndex(m => {
        const d = m.getExtData()
        return d && d.id === record.id
      })
      if (idx > -1) {
        this.map.remove(this.markerAMapInstances[idx])
        this.markerAMapInstances.splice(idx, 1)
        this.markerSource.splice(idx, 1)
      }
      this.selectedMarker = null
      this.infoWindow.close()
    },
    focusMarker(item) {
      if (!this.map) return
      this.map.setCenter(item.position)
      this.map.setZoom(15)
      this.selectedMarker = item
    },

    generateNewTrackPath() {
      const center = [116.397428, 39.90923]
      const path = [[...center]]
      let lng = center[0], lat = center[1]
      for (let i = 0; i < 25; i++) {
        lng += (Math.random() - 0.5) * 0.008
        lat += (Math.random() - 0.5) * 0.008
        path.push([+lng.toFixed(6), +lat.toFixed(6)])
      }
      this.trackPath = path
      this.isPlaying = false; this.isPaused = false; this.isStopped = false
      this.showTrackLine()
      if (this.map) this.map.setFitView(null, false, [40, 40, 40, 40])
    },
    showTrackLine() {
      if (!this.AMap || !this.map || this.trackPath.length < 2) return
      this.destroyTrack()
      this.trackLine = new this.AMap.Polyline({ map: this.map, path: this.trackPath, showDir: true, strokeColor: '#28F', strokeOpacity: 0.8, strokeWeight: 6 })
      this.passedPolyline = new this.AMap.Polyline({ map: this.map, strokeColor: '#AF5', strokeOpacity: 1, strokeWeight: 6, path: [] })
      this.trackMarker = new this.AMap.Marker({
        map: this.map, position: this.trackPath[0],
        content: '<div style="background:#409EFF;color:#fff;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:bold;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);"><svg viewBox="0 0 1024 1024" width="18" height="18" fill="white"><path d="M512 128c-141.4 0-256 114.6-256 256 0 192 256 512 256 512s256-320 256-512c0-141.4-114.6-256-256-256z m0 384c-70.7 0-128-57.3-128-128s57.3-128 128-128 128 57.3 128 128-57.3 128-128 128z"/></svg></div>',
        offset: new this.AMap.Pixel(-16, -16),
      })
      if (this.trackPath.length > 1) {
        const p1 = this.trackPath[0], p2 = this.trackPath[1]
        const dx = p2[0] - p1[0], dy = p2[1] - p1[1]
        this.trackMarker.setAngle(90 - (Math.atan2(dy, dx) * 180 / Math.PI))
      }
      this.trackMarker.on('moving', (e) => {
        if (this.isStopped) return
        if (this.passedPolyline) this.passedPolyline.setPath(e.passedPath)
      })
    },
    destroyTrack() {
      if (this.trackLine)   { this.trackLine.setMap(null);   this.trackLine = null }
      if (this.passedPolyline) { this.passedPolyline.setMap(null); this.passedPolyline = null }
      if (this.trackMarker) { this.trackMarker.stopMove?.(); this.trackMarker.setMap(null); this.trackMarker = null }
    },
    startTrackAnimation() {
      if (!this.trackMarker || !this.trackPath || this.trackPath.length < 2) return this.generateNewTrackPath()
      if (this.isPaused) return this.resumeTrackAnimation()
      this.showTrackLine()
      this.isPlaying = true; this.isPaused = false; this.isStopped = false
      this.trackMarker.moveAlong(this.trackPath, { speed: this.trackSpeed, autoRotation: true })
      this.trackMarker.once('moveend', () => {
        if (!this.isStopped) { this.isPlaying = false; this.isPaused = false; this.$toast.success('轨迹回放完成') }
      })
    },
    pauseTrackAnimation() {
      if (this.trackMarker?.pauseMove) { this.trackMarker.pauseMove(); this.isPaused = true; this.isPlaying = false }
    },
    resumeTrackAnimation() {
      if (this.trackMarker?.resumeMove) { this.trackMarker.resumeMove(); this.isPaused = false; this.isPlaying = true }
    },
    stopTrackAnimation() {
      if (this.trackMarker) { this.isStopped = true; this.trackMarker.stopMove() }
      this.isPlaying = false; this.isPaused = false
      this.showTrackLine()
    },
    fitTrackView() {
      if (this.map && this.trackPath.length > 0) this.map.setFitView(null, false, [40, 40, 40, 40])
    },

    handleCoordConvert() {
      if (!this.coordInput) return this.$toast.warning('请输入坐标')
      const cleaned = this.coordInput.replace(/['"\[\]\s]/g, '')
      const parts = cleaned.split(',')
      if (parts.length !== 2) return this.$toast.warning('格式错误，请输入 经度,纬度')
      const lng = parseFloat(parts[0]), lat = parseFloat(parts[1])
      if (isNaN(lng) || isNaN(lat)) return this.$toast.warning('坐标格式错误')

      let wgs84, gcj02, bd09
      if (this.fromCoord === 'wgs84') {
        wgs84 = { lng, lat }
        gcj02 = this.wgs84ToGcj02(lng, lat)
        bd09 = this.gcj02ToBd09(gcj02.lng, gcj02.lat)
      } else if (this.fromCoord === 'gcj02') {
        gcj02 = { lng, lat }
        wgs84 = this.gcj02ToWgs84(lng, lat)
        bd09 = this.gcj02ToBd09(lng, lat)
      } else {
        bd09 = { lng, lat }
        gcj02 = this.bd09ToGcj02(lng, lat)
        wgs84 = this.gcj02ToWgs84(gcj02.lng, gcj02.lat)
      }
      this.coordResults = {
        show: true,
        wgs84: `${wgs84.lng.toFixed(6)}, ${wgs84.lat.toFixed(6)}`,
        gcj02: `${gcj02.lng.toFixed(6)}, ${gcj02.lat.toFixed(6)}`,
        bd09: `${bd09.lng.toFixed(6)}, ${bd09.lat.toFixed(6)}`,
      }
      this.map.setCenter([gcj02.lng, gcj02.lat])
      this.map.setZoom(14)
      this.placeClickMarker([gcj02.lng, gcj02.lat])
      this.clickCoordinateText = this.formatLngLat([gcj02.lng, gcj02.lat])
      this.reverseGeocode([gcj02.lng, gcj02.lat])
    },

    outOfChina(lng, lat) { return !(lng > 73.66 && lng < 135.05 && lat > 3.86 && lat < 53.55) },
    transformLat(lng, lat) {
      let ret = -100 + 2 * lng + 3 * lat + 0.2 * lat * lat + 0.1 * lng * lat + 0.2 * Math.sqrt(Math.abs(lng))
      ret += (20 * Math.sin(6 * lng * Math.PI) + 20 * Math.sin(2 * lng * Math.PI)) * 2 / 3
      ret += (20 * Math.sin(lat * Math.PI) + 40 * Math.sin(lat / 3 * Math.PI)) * 2 / 3
      ret += (160 * Math.sin(lat / 12 * Math.PI) + 320 * Math.sin(lat * Math.PI / 30)) * 2 / 3
      return ret
    },
    transformLng(lng, lat) {
      let ret = 300 + lng + 2 * lat + 0.1 * lng * lng + 0.1 * lng * lat + 0.1 * Math.sqrt(Math.abs(lng))
      ret += (20 * Math.sin(6 * lng * Math.PI) + 20 * Math.sin(2 * lng * Math.PI)) * 2 / 3
      ret += (20 * Math.sin(lng * Math.PI) + 40 * Math.sin(lng / 3 * Math.PI)) * 2 / 3
      ret += (150 * Math.sin(lng / 12 * Math.PI) + 300 * Math.sin(lng / 30 * Math.PI)) * 2 / 3
      return ret
    },
    wgs84ToGcj02(lng, lat) {
      if (this.outOfChina(lng, lat)) return { lng, lat }
      let dLat = this.transformLat(lng - 105, lat - 35)
      let dLng = this.transformLng(lng - 105, lat - 35)
      const radLat = lat / 180 * Math.PI
      let magic = Math.sin(radLat)
      magic = 1 - 0.00669342162296594323 * magic * magic
      const sqrtMagic = Math.sqrt(magic)
      dLat = (dLat * 180) / ((6378245 * (1 - 0.00669342162296594323)) / (magic * sqrtMagic) * Math.PI)
      dLng = (dLng * 180) / (6378245 / sqrtMagic * Math.cos(radLat) * Math.PI)
      return { lng: lng + dLng, lat: lat + dLat }
    },
    gcj02ToWgs84(lng, lat) {
      if (this.outOfChina(lng, lat)) return { lng, lat }
      const wgs = this.wgs84ToGcj02(lng, lat)
      return { lng: lng * 2 - wgs.lng, lat: lat * 2 - wgs.lat }
    },
    gcj02ToBd09(lng, lat) {
      const xPI = Math.PI * 3000 / 180
      const z = Math.sqrt(lng * lng + lat * lat) + 0.00002 * Math.sin(lat * xPI)
      const theta = Math.atan2(lat, lng) + 0.000003 * Math.cos(lng * xPI)
      return { lng: z * Math.cos(theta) + 0.0065, lat: z * Math.sin(theta) + 0.006 }
    },
    bd09ToGcj02(lng, lat) {
      const xPI = Math.PI * 3000 / 180
      const x = lng - 0.0065, y = lat - 0.006
      const z = Math.sqrt(x * x + y * y) - 0.00002 * Math.sin(y * xPI)
      const theta = Math.atan2(y, x) - 0.000003 * Math.cos(x * xPI)
      return { lng: z * Math.cos(theta), lat: z * Math.sin(theta) }
    },

    formatLngLat(pos) {
      if (!pos || pos.length < 2) return '-'
      return `${Number(pos[0]).toFixed(6)}, ${Number(pos[1]).toFixed(6)}`
    },
  },
}
</script>

<style scoped>
.integrated-map {
  height: 100%; display: flex; flex-direction: column;
  background: #f0f2f5; position: relative; overflow: hidden;
}
.map-toolbar {
  display: flex; justify-content: space-between; align-items: center;
  padding: 8px 16px;
  background: linear-gradient(135deg, #3f51b5 0%, #5c6bc0 100%);
  color: #fff; flex-shrink: 0; z-index: 10;
}
.toolbar-left { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.brand { font-size: 16px; font-weight: 700; letter-spacing: 0.5px; }
.separator { color: rgba(255,255,255,0.4); font-size: 14px; }
.subtitle { font-size: 12px; opacity: 0.8; }
.engine-tag { font-size: 11px; }
.toolbar-right { display: flex; gap: 8px; flex-wrap: wrap; }
.status-chip {
  padding: 4px 10px; border-radius: 12px; background: rgba(255,255,255,0.15);
  font-size: 11px; white-space: nowrap;
}
.map-body { flex: 1; display: flex; position: relative; overflow: hidden; }
.sidebar-toggle {
  position: absolute; left: 0; top: 50%; transform: translateY(-50%);
  z-index: 20; width: 24px; height: 48px; background: #fff;
  border: 1px solid #dcdfe6; border-left: none; border-radius: 0 6px 6px 0;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; box-shadow: 2px 0 8px rgba(0,0,0,0.06); color: #606266;
}
.sidebar-toggle:hover { color: #409EFF; }
.map-sidebar {
  width: 340px; background: #fff; border-right: 1px solid #e4e7ed;
  flex-shrink: 0; overflow-y: auto; z-index: 10; position: relative;
}
.map-sidebar::-webkit-scrollbar { width: 5px; }
.map-sidebar::-webkit-scrollbar-thumb { background: #c0c4cc; border-radius: 4px; }
.map-sidebar :deep(.el-tabs__header) {
  margin: 0; padding: 0 12px; background: #f5f7fa; border-bottom: 1px solid #e4e7ed;
}
.map-sidebar :deep(.el-tabs__item) { padding: 0 10px; height: 36px; line-height: 36px; font-size: 12px; }
.map-sidebar :deep(.el-tab-pane) { padding: 12px; }
.panel-section { margin-bottom: 16px; padding-bottom: 12px; border-bottom: 1px solid #f0f0f0; }
.panel-section:last-child { border-bottom: none; margin-bottom: 0; }
.panel-title {
  font-size: 13px; font-weight: 600; color: #303133;
  margin-bottom: 10px; display: flex; align-items: center; gap: 6px;
}
.panel-title i { color: #409EFF; font-size: 14px; }
.btn-row { display: flex; gap: 8px; flex-wrap: wrap; }
.field-row { margin-bottom: 10px; }
.field-row label { display: block; font-size: 12px; color: #606266; margin-bottom: 4px; }
.info-grid { display: flex; flex-direction: column; gap: 8px; }
.info-item { padding: 10px; border-radius: 8px; background: #f7fbff; border: 1px solid #e5edf6; }
.info-item span { display: block; font-size: 11px; color: #909399; margin-bottom: 4px; }
.info-item strong { font-size: 13px; color: #303133; word-break: break-all; }
.address-text { display: block; min-height: 20px; }
.marker-list { display: flex; flex-direction: column; gap: 6px; max-height: 200px; overflow-y: auto; }
.marker-list-item {
  display: flex; align-items: center; gap: 8px; padding: 8px 10px;
  border-radius: 6px; background: #f5f7fa; cursor: pointer; transition: background 0.2s;
}
.marker-list-item:hover { background: #ecf5ff; }
.marker-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.marker-info { flex: 1; min-width: 0; }
.marker-info strong { display: block; font-size: 13px; color: #303133; }
.marker-info span { font-size: 11px; color: #909399; }
.marker-detail { display: flex; flex-direction: column; gap: 8px; }
.detail-row {
  display: flex; justify-content: space-between; padding-bottom: 6px;
  border-bottom: 1px solid #f0f2f5; font-size: 13px; color: #606266;
}
.detail-row strong { color: #303133; }
.empty-tip { color: #909399; font-size: 13px; text-align: center; padding: 16px; }
.speed-label { font-size: 11px; color: #909399; margin-top: 4px; }
.coord-results { margin-top: 12px; display: flex; flex-direction: column; gap: 8px; }
.result-row { display: flex; align-items: center; gap: 8px; }
.result-label { font-size: 11px; color: #606266; flex-shrink: 0; width: 52px; }
.map-stage { flex: 1; min-height: 400px; }
.map-stage :deep(.amap-container) { width: 100%; height: 100%; }
.layer-switcher-float {
  position: absolute; right: 16px; bottom: 40px; z-index: 20;
  display: flex; gap: 4px; flex-direction: column;
}
.layer-item {
  padding: 6px 14px; border-radius: 6px; background: #fff;
  box-shadow: 0 2px 8px rgba(0,0,0,0.12); font-size: 12px; color: #606266;
  cursor: pointer; text-align: center; transition: all 0.2s; border: 2px solid transparent;
}
.layer-item:hover { color: #409EFF; }
.layer-item.active { border-color: #409EFF; color: #409EFF; font-weight: 600; }
.map-footer {
  display: flex; align-items: center; gap: 20px; padding: 4px 16px;
  background: #fff; border-top: 1px solid #e4e7ed; font-size: 11px; color: #909399; flex-shrink: 0;
}
.map-footer i { margin-right: 4px; }
.footer-hint { margin-left: auto; font-family: monospace; opacity: 0.6; }
.slide-sidebar-enter-active, .slide-sidebar-leave-active { transition: width 0.3s ease, opacity 0.3s ease; overflow: hidden; }
.slide-sidebar-enter, .slide-sidebar-leave-to { width: 0 !important; opacity: 0; }
</style>
